import {InputValidatorService} from './InputValidationService';

const VALID_INPUT_CONTAINER_ATTRIBUTE = 'data-input-container';
const INPUT_ERROR_MESSAGE_SELECTOR = 'data-input-error';
const ACTIVE_CLASS = 'active';
const VALID_CLASS = 'valid';
const INVALID_CLASS = 'invalid';

function initializationErrorMessage(reason) {
    return `class Input failed init : ${reason}`;
}

const inputInitializationChecks = [
    {
        check: (container) => container.hasAttribute(VALID_INPUT_CONTAINER_ATTRIBUTE),
        message: (container) => initializationErrorMessage(`${container} is not a valid input container`),
    },
    {
        check: (container) => container.querySelector('input, select') !== null,
        message: (container) => initializationErrorMessage(`${container} does not contain input element`),
    },
    {
        check: (container) => container.tagName === 'LABEL' || container.querySelector('label') !== null,
        message: (container) => initializationErrorMessage(`${container} does not contain or is not a label element`),
    },
];

export class Input {
    constructor(inputContainer, {errorMessages} = {}) {
        this.checkContainerValidity(inputContainer);

        this.root = inputContainer;
        this.inputEl = this.root.querySelector('input, select');
        this.validationService = new InputValidatorService({errorMessages});
        this.validationHasBeenCalled = false;

        this.setUpErrorMessageContainer();
        this.addListeners();
        this.setContainerStateDependingOnInputValue();
    }

    checkContainerValidity(container) {
        inputInitializationChecks.forEach((conf) => {
            if (!conf.check(container)) {
                throw new Error(conf.message(container));
            }
        });
    }

    get value() {
        return this.inputEl.value;
    }

    get errorMessage() {
        return this._errorMessage;
    }

    set errorMessage(message) {
        this._errorMessage = message;
    }

    get errorMessageContainer() {
        return this.root.querySelector(`[${INPUT_ERROR_MESSAGE_SELECTOR}]`);
    }

    setContainerStateDependingOnInputValue() {
        this.value ? this.setActive() : this.setInactive();
    }

    setActive() {
        this.root.classList.add(ACTIVE_CLASS);
    }

    setInactive() {
        this.root.classList.remove(ACTIVE_CLASS);
    }

    displayErrorMessage(errorMessage) {
        this.errorMessage = errorMessage;
        this.updateErrorMessageContainerContent();
    }

    resetErrorMessage() {
        this.errorMessage = '';
        this.updateErrorMessageContainerContent();
    }

    updateErrorMessageContainerContent() {
        this.errorMessageContainer.innerHTML = this.errorMessage;
    }

    setValid() {
        this.resetErrorMessage();
        this.setRootStateToValid();
    }

    setRootStateToValid() {
        this.root.classList.remove(INVALID_CLASS);
        this.root.classList.add(VALID_CLASS);
    }

    setInvalid(errorMessage) {
        this.displayErrorMessage(errorMessage);
        this.setRootStateToInvalid();
    }

    setRootStateToInvalid() {
        this.root.classList.remove(VALID_CLASS);
        this.root.classList.add(INVALID_CLASS);
    }

    resetRootState() {
        this.root.classList.remove(VALID_CLASS);
        this.root.classList.remove(INVALID_CLASS);
    }

    getValidity() {
        return this.validationService.validateInput(this.inputEl);
    }

    validate() {
        const validity = this.getValidity();

        if (validity.valid) {
            this.setValid();

            return;
        }

        this.setInvalid(validity.errorMessage);
    }

    validateIfValidationHasAlreadyBeenCalled() {
        if (!this.validationHasBeenCalled) {
            return;
        }

        this.validate();
    }

    validateWithoutNegativeFeedback() {
        if (this.validationHasBeenCalled) {
            return;
        }

        const validity = this.validationService.validateInput(this.inputEl);

        if (validity.valid) {
            this.setValid();

            return;
        }

        this.resetRootState();
    }

    setUpErrorMessageContainer() {
        const errorMessageContainer = `<i ${INPUT_ERROR_MESSAGE_SELECTOR}></i>`;
        this.root.insertAdjacentHTML('beforeend', errorMessageContainer);
    }

    addListeners() {
        this.inputEl.addEventListener('input', this.validateWithoutNegativeFeedback.bind(this));
        this.inputEl.addEventListener('input', this.validateIfValidationHasAlreadyBeenCalled.bind(this));
        this.inputEl.addEventListener('focus', this.setActive.bind(this));
        this.inputEl.addEventListener('blur', this.setContainerStateDependingOnInputValue.bind(this));
    }
}
